import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FolderOpen, Edit3, Trash2, Layers, Loader2, Archive, ArchiveRestore } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ICON_OPTIONS = ["📁","📊","🧪","💼","📝","🎯","🏢","💰","📞","🎓","🔍","⚡","🌟","🚀","🛠️","📈","🤝","🗂️","💡","🔥"];
const COLOR_OPTIONS = ["#0A192F","#B7955B","#0891b2","#dc2626","#16a34a","#7c3aed","#ea580c","#0284c7","#be185d","#475569"];

export default function WorkspacesListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const canModify = ["admin","leader"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspaces", { params: { include_archived: showArchived } });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Errore caricamento aree di lavoro");
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async (data) => {
    try {
      await api.post("/workspaces", data);
      toast.success("Area di lavoro creata");
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    }
  };

  const onUpdate = async (id, data) => {
    try {
      await api.patch(`/workspaces/${id}`, data);
      toast.success("Aggiornato");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  const onDelete = async (w) => {
    if (w.board_count > 0 && !window.confirm(`L'area "${w.name}" contiene ${w.board_count} board. Eliminare TUTTO?`)) return;
    if (w.board_count === 0 && !window.confirm(`Eliminare "${w.name}"?`)) return;
    try {
      await api.delete(`/workspaces/${w.id}`, { params: { cascade: w.board_count > 0 } });
      toast.success("Eliminata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const onArchive = async (w) => {
    try {
      await api.patch(`/workspaces/${w.id}`, { archived: !w.archived });
      toast.success(w.archived ? "Ripristinata" : "Archiviata");
      load();
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-1" style={{ color: "var(--gold)" }}>Modulo Monday-style</div>
            <h1 className="font-serif text-4xl text-slate-900">Aree di Lavoro</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Crea i tuoi flussi personalizzati — tabelle, colonne, gruppi — completamente modificabili dall'interfaccia.
              Ogni area può contenere più board.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowArchived(!showArchived)}
              data-testid="ws-toggle-archived"
              className="px-3 py-2 text-xs border border-slate-300 rounded-[3px] hover:bg-slate-50 flex items-center gap-1.5"
            >
              {showArchived ? <ArchiveRestore className="w-3.5 h-3.5"/> : <Archive />}
              {showArchived ? "Mostra attive" : "Mostra archivio"}
            </button>
            {canModify && (
              <button
                onClick={() => setCreating(true)}
                data-testid="ws-create-btn"
                className="px-4 py-2 text-sm text-white rounded-[3px] flex items-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} 
              >
                <Plus className="w-4 h-4" /> Nuova area
              </button>
            )}
          </div>
        </div>

        {loading && <div className="flex justify-center py-20 w-6 h-6 animate-spin text-[#B7955B]"><Loader2 /></div>}

        {!loading && items.length === 0 && (
          <div className="border-2 border-dashed border-slate-200 rounded-[3px] py-16 text-center" data-testid="ws-empty">
            <FolderOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <div className="font-serif text-xl text-slate-700">Nessuna area di lavoro</div>
            <p className="text-sm text-slate-500 mt-1">Crea la prima per iniziare a costruire le tue tabelle personalizzate.</p>
            {canModify && (
              <button onClick={() => setCreating(true)} className="mt-4 px-4 py-2 text-sm bg-[#0A192F] text-white rounded-[3px] hover:bg-[#172A45] inline-flex items-center gap-2">
                <Plus className="w-4 h-4"/> Crea ora
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((w) => (
            <WorkspaceCard
              key={w.id}
              w={w}
              onOpen={() => navigate(`/aree-di-lavoro/${w.id}`)}
              onEdit={canModify ? () => setEditTarget(w) : null}
              onDelete={canModify ? () => onDelete(w) : null}
              onArchive={canModify ? () => onArchive(w) : null}
            />
          ))}
        </div>

        {creating && (
          <WorkspaceFormDialog
            initial={null}
            onClose={() => setCreating(false)}
            onSubmit={onCreate}
          />
        )}
        {editTarget && (
          <WorkspaceFormDialog
            initial={editTarget}
            onClose={() => setEditTarget(null)}
            onSubmit={(data) => onUpdate(editTarget.id, data)}
          />
        )}
      </div>
    </AppLayout>
  );
}

function WorkspaceCard({ w, onOpen, onEdit, onDelete, onArchive }) {
  return (
    <div
      data-testid={`ws-card-${w.id}`}
      className="group bg-white border border-slate-200 hover:border-[#B7955B] hover:shadow-sm rounded-[3px] p-5 transition cursor-pointer relative"
      onClick={onOpen}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="w-12 h-12 rounded-[3px] flex items-center justify-center text-2xl shrink-0"
            style={{ backgroundColor: `${w.color}15`, border: `1px solid ${w.color}30` }}
          >
            {w.icon || "📁"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg text-slate-900 truncate flex items-center gap-1.5">
              {w.name}
              {w.archived && <span className="text-[9px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-[3px]">archiviato</span>}
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
              <Layers className="w-3 h-3" /> {w.board_count || 0} board
            </div>
          </div>
        </div>
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition" onClick={(e) => e.stopPropagation()}>
          {onEdit && (
            <button onClick={onEdit} data-testid={`ws-edit-${w.id}`} title="Modifica" className="p-1.5 hover:bg-slate-100 rounded-[3px] w-3.5 h-3.5 text-slate-500"><Edit3 /></button>
          )}
          {onArchive && (
            <button onClick={onArchive} data-testid={`ws-archive-${w.id}`} title={w.archived ? "Ripristina" : "Archivia"} className="p-1.5 hover:bg-slate-100 rounded-[3px]">
              {w.archived ? <ArchiveRestore className="w-3.5 h-3.5 text-slate-500"/> : <Archive />}
            </button>
          )}
          {onDelete && (
            <button onClick={onDelete} data-testid={`ws-delete-${w.id}`} title="Elimina" className="p-1.5 hover:bg-rose-100 rounded-[3px] w-3.5 h-3.5 text-rose-600"><Trash2 /></button>
          )}
        </div>
      </div>
      {w.description && <p className="text-xs text-slate-500 line-clamp-2">{w.description}</p>}
    </div>
  );
}

function WorkspaceFormDialog({ initial, onClose, onSubmit }) {
  const [name, setName] = useState(initial?.name || "");
  const [icon, setIcon] = useState(initial?.icon || "📁");
  const [color, setColor] = useState(initial?.color || COLOR_OPTIONS[0]);
  const [description, setDescription] = useState(initial?.description || "");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Nome obbligatorio"); return; }
    setSubmitting(true);
    await onSubmit({ name: name.trim(), icon, color, description });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-[3px] shadow-2xl w-full max-w-lg" data-testid="ws-form-dialog">
        <div className="px-5 py-4 border-b border-[#F0F2F5]">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: "var(--gold)" }}>{initial ? "Modifica" : "Crea"}</div>
          <h2 className="font-serif text-xl mt-0.5">{initial ? `Modifica "${initial.name}"` : "Nuova area di lavoro"}</h2>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1 block">Nome</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="es. Recupero Crediti"
              data-testid="ws-form-name"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-2 focus:ring-slate-900/8"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1 block">Icona</label>
            <div className="flex flex-wrap gap-1.5" data-testid="ws-form-icons">
              {ICON_OPTIONS.map((ic) => (
                <button key={ic} type="button" onClick={() => setIcon(ic)}
                  className={`w-9 h-9 text-lg rounded-[3px] border ${icon === ic ? "border-[#B7955B] bg-[#B7955B]/10" : "border-slate-200 hover:border-slate-400"}`}
                >{ic}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1 block">Colore</label>
            <div className="flex flex-wrap gap-1.5" data-testid="ws-form-colors">
              {COLOR_OPTIONS.map((col) => (
                <button key={col} type="button" onClick={() => setColor(col)} style={{ backgroundColor: col }}
                  className={`w-8 h-8 rounded-[3px] border-2 ${color === col ? "border-slate-900" : "border-transparent"}`}/>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1 block">Descrizione (opzionale)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-2 focus:ring-slate-900/8"/>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#F0F2F5] bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-slate-300 rounded-[3px] hover:bg-white">Annulla</button>
          <button onClick={submit} disabled={submitting} data-testid="ws-form-submit"
            className="px-3 py-2 text-sm bg-[#0A192F] text-white rounded-[3px] hover:bg-[#172A45] disabled:opacity-60 flex items-center gap-1.5">
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin"/>}
            {initial ? "Salva modifiche" : "Crea"}
          </button>
        </div>
      </div>
    </div>
  );
}
